import React, {
  FC,
  ReactNode,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { IconCalendar } from '../components/icons';
import { PageHeader } from '../components/PageHeader';
import { useInteractionTimeout } from '../hooks/useInteractionTimeout';
import { useConfig } from '../providers/ConfigProvider';
import { useNavigateToDefaultView } from '../hooks/useNavigateToDefaultView';
import { QuickBookingRow } from '../components/Booking/QuickBookingRow';
import { SelectedBooking } from '../types/booking';
import { NfcTagReader } from '../components/Booking/NfcTagReader';
import { HeaderContent } from '../components/Booking/HeaderContent';
import { useUserBookings } from '../hooks/useBookings';
import { useResources } from '../hooks/useResources';
import { BookingModal } from '../components/Booking/BookingModal';
import { UserBookingsList } from '../components/Booking/UserBookingsList';
import { useAuth } from '../hooks/useAuth';
import { useApiClient } from '../hooks/useApiClient';
import { useIdentity } from '../providers/IdentityProvider';

interface Props {
  portrait?: boolean;
}

export const BookingView: FC<Props> = ({ portrait }) => {
  const [selectedBooking, setSelectedBooking] =
    useState<SelectedBooking | null>(null);
  const [loginError, setLoginError] = useState<string>();
  const resourcesRef = useRef<HTMLDivElement>(null);
  const myBookingsRef = useRef<HTMLDivElement>(null);
  const { interactionTimeout, nfcKeyTimeout } = useConfig();
  const apiClient = useApiClient();
  const { id: deviceId } = useIdentity(
    useCallback((state: { id?: string }) => ({ id: state.id }), []),
  );
  const { isAuthenticated, login, logout } = useAuth();
  const navigateToDefaultView = useNavigateToDefaultView([
    resourcesRef,
    myBookingsRef,
  ]);
  useInteractionTimeout(nfcKeyTimeout, logout);
  useInteractionTimeout(interactionTimeout, () => {
    setSelectedBooking(null);
    navigateToDefaultView();
  });

  const { data: resources = [] } = useResources();
  const { data: userBookings = [] } = useUserBookings(resources);

  const updateLoginError = useCallback((error: string | undefined) => {
    setLoginError(error);
    setTimeout(() => {
      setLoginError(undefined);
    }, 5000);
  }, []);

  const handleNfcAuthenticated = useCallback(
    (key: string) => {
      updateLoginError(undefined);
      if (!deviceId) {
        console.error('[BookingView] deviceId missing, cannot validate key.');
        updateLoginError('unknown_error');
        return;
      }
      console.log(
        `[BookingView] Validating NFC key ${key} for device ${deviceId}`,
      );
      // FIXME: Should probably use the useUserBookings hook instead of calling the API directly?
      apiClient.public.devices
        .getIloqUserBookings(deviceId, key)
        .then((data) => {
          console.log(`[BookingView] NFC key ${key} validated successfully.`);
          login(key, data.pin || '');
        })
        .catch((err) => {
          console.error('[BookingView] NFC key validation failed:', err);
          updateLoginError('wrong_key');
          logout();
        });
    },
    [updateLoginError, deviceId, login, apiClient.public.devices, logout],
  );

  const myBookingsContent = useMemo(() => {
    if (!isAuthenticated) {
      if (selectedBooking) {
        return null;
      }

      return (
        <div className="flex size-full flex-col p-3 items-center justify-center text-center rounded">
          <NfcTagReader
            className="p-5"
            onAuthenticated={handleNfcAuthenticated}
          />
          {loginError && (
            <div className="text-red-600 mt-2 mb-4 bg-red-50 p-3 border border-red-400">
              {loginError === 'key_not_found' && (
                <FormattedMessage defaultMessage="Nyckeln kunde inte hittas" />
              )}
              {loginError === 'wrong_key' && (
                <FormattedMessage defaultMessage="Fel nyckel användes" />
              )}
              {loginError === 'unknown_error' && (
                <FormattedMessage defaultMessage="Ett fel uppstod vid inloggning" />
              )}
            </div>
          )}
          <p className="font-bold text-xl">
            <FormattedMessage defaultMessage="Håll fram din digitala nyckel mot mitten av loggan på nedre delen av skärmen för att se dina kommande bokningar" />
          </p>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4 pb-8">
        <UserBookingsList
          resources={resources}
          onBookingCancel={(booking, resource) =>
            setSelectedBooking({
              resource,
              slot: booking,
              mode: 'cancel',
            })
          }
        />
      </div>
    );
  }, [
    handleNfcAuthenticated,
    isAuthenticated,
    loginError,
    resources,
    selectedBooking,
  ]);

  return (
    <div className="size-full">
      <PageHeader
        icon={<IconCalendar />}
        title={<FormattedMessage defaultMessage="Bokning" />}
        slim={portrait}
        content={<HeaderContent />}
      />
      <main
        className="flex gap-8 px-12 w-full"
        style={{ height: `calc(100% - ${portrait ? 90 : 140}px)` }}
      >
        <div
          ref={resourcesRef}
          className="flex flex-col gap-3 w-full overflow-auto"
        >
          <TopSection
            title={<FormattedMessage defaultMessage="Bokningsbara resurser" />}
            desc={
              <FormattedMessage defaultMessage="Nedan är en lista av vad som går att boka" />
            }
          />
          <div className="flex flex-col gap-10 flex-1 pb-8">
            {resources.map((r) => {
              return (
                <QuickBookingRow
                  key={r.name}
                  resource={r}
                  userBookings={userBookings}
                  onSlotSelect={(slot) =>
                    setSelectedBooking({
                      resource: r,
                      slot,
                      mode: slot.status === 'available' ? 'book' : 'cancel',
                    })
                  }
                />
              );
            })}
          </div>
        </div>
        <div
          ref={myBookingsRef}
          className="flex flex-col gap-3 w-80 min-w-80 max-w-80 overflow-auto"
        >
          <TopSection
            title={<FormattedMessage defaultMessage="Mina bokningar" />}
            desc={
              <FormattedMessage defaultMessage="Nedan är dina kommande bokningar" />
            }
          />
          {myBookingsContent}
        </div>
      </main>
      {selectedBooking && (
        <BookingModal
          mode={selectedBooking.mode}
          resource={selectedBooking.resource}
          timeSlot={selectedBooking.slot}
          onClose={() => setSelectedBooking(null)}
        />
      )}
    </div>
  );
};

interface TopSectionProps {
  title: ReactNode;
  desc: ReactNode;
}
const TopSection: FC<TopSectionProps> = ({ title, desc }) => {
  return (
    <div className="flex flex-col gap-2">
      <h2 className="font-medium text-gray-900">{title}</h2>
      <p className="text-gray-medium">{desc}</p>
    </div>
  );
};
